import * as React from 'react';
import { useState } from 'react';

import {
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useTheme,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FiRefreshCw } from 'react-icons/fi';

import { AppButton } from '@/elements';

import { CloseModalIconButton } from '@/components/IconButton/close-modal';

import { useCountdown } from '@/hooks/useCountdown';

interface ISignupResendModal {
  isOpen: boolean;
  email: string;
  isLoading: boolean;
  onClose(): void;
  onResend(): void;
}

const REFRESH_DELAY = 60000;
/**
 * Modal component for resend verification link
 * @component
 * @param    {ISignupResendModal}   props     Component props
 * @param    {boolean}  props.isOpen  Modal open or not
 * @param    {string}  props.email  Email to send the link for verification
 * @param    {void}  props.onClose  Function to close modal
 * @param    {void}  props.onResend  Function to resend verification link
 * @param    {boolean}  props.isLoading  Is request loading or not
 * @example
 * <SignupResendModal
 *    isOpen={isOpen}
 *    email={email}
 *    onClose={onClose}
 *    onResend={onResend}
 *    isLoading={isLoading}
 * />
 */
export const SignupResendModal: React.FC<ISignupResendModal> = ({
  isOpen,
  email,
  onClose,
  onResend,
  isLoading,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [refreshDelay, setRefreshDelay] = useState(Date.now());

  const { timeLeftSecs: refreshSecsLeft } = useCountdown({
    stopAt: refreshDelay,
    start: true,
    key: 'refreshTimer',
  });

  const onResendHandler = async () => {
    await onResend();
    setRefreshDelay(Date.now() + REFRESH_DELAY);
  };

  return (
    <Modal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      size='2xl'
    >
      <ModalOverlay />

      <ModalContent>
        <CloseModalIconButton onClick={onClose} />

        <ModalBody pb='1.875rem' pt='lg' px='lg'>
          <Flex align='center' justify='center'>
            <FiRefreshCw color={theme.colors.app.pink} size='3.75rem' />
          </Flex>

          <Heading
            as='h3'
            colorScheme='app.pink'
            fontSize='3xl'
            my='lg'
            textAlign='center'
          >
            {t('signup.resendModal.title')}
          </Heading>

          <Box
            sx={{
              width: '100%',
              maxWidth: '34.375rem',
              margin: '0 auto',
              textAlign: 'center',
              paddingBottom: '1.875rem',
              fontSize: 'md',
            }}
          >
            <Box>{t('signup.resendModal.clickToSend')}</Box>

            <Box>{t('signup.resendModal.sendToEmail')}</Box>

            <Box fontWeight='700'>{email}</Box>

            <Box py='lg'>
              <AppButton
                isDisabled={isLoading || !!refreshSecsLeft}
                isLoading={isLoading}
                onClick={onResendHandler}
                size='lg'
                variant='primary'
              >
                {t('signup.resendModal.button')}
              </AppButton>

              {refreshSecsLeft && (
                <Flex justify='center' pt='md'>
                  <Text>Send again after {refreshSecsLeft} seconds</Text>
                </Flex>
              )}
            </Box>

            <Box>
              {t('signup.resendModal.justClickText')}{' '}
              <Box as='span' fontWeight='700'>
                {t('signup.resendModal.checkSpam')}
              </Box>{' '}
              {t('signup.resendModal.ifYouCantSee')}
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
